
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BookingStatus {
  status_key: string;
  display_name: string;
  description: string;
  color: string;
  sort_order: number;
  category: string; // 'booking', 'quotation', 'payment', 'fulfillment'
}

export interface StatusTransition {
  to_status: string;
  display_name: string;
  color: string;
  category: string; // NEW: Include category in transitions
  is_automatic: boolean;
  requires_admin: boolean;
  description: string;
}

export const useBookingStatuses = () => {
  return useQuery({
    queryKey: ['booking-statuses'],
    queryFn: async (): Promise<BookingStatus[]> => {
      console.log('[BOOKING-STATUSES] Fetching booking statuses');
      const { data, error } = await supabase.rpc('get_valid_booking_statuses');
      
      if (error) {
        console.error('[BOOKING-STATUSES] Error fetching booking statuses:', error);
        throw error;
      }
      
      console.log('[BOOKING-STATUSES] Fetched booking statuses:', data);
      return data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

// Open transitions: any status can transition to any other status
export const useStatusTransitions = (currentStatus: string) => {
  const { data: allStatuses } = useBookingStatuses();
  
  return useQuery({
    queryKey: ['status-transitions', currentStatus],
    queryFn: async (): Promise<StatusTransition[]> => {
      if (!currentStatus || !allStatuses) return [];
      
      console.log('[STATUS-TRANSITIONS] Open transitions - all statuses available (excluding current):', currentStatus);
      
      // All statuses are valid transitions (except current status)
      const transitions: StatusTransition[] = allStatuses
        .filter(status => status.status_key !== currentStatus)
        .map(status => ({
          to_status: status.status_key,
          display_name: status.display_name,
          color: status.color,
          category: status.category,
          is_automatic: false,
          requires_admin: false,
          description: `Change to ${status.display_name}`
        }));
      
      console.log('[STATUS-TRANSITIONS] Available transitions:', transitions.length);
      return transitions;
    },
    enabled: !!currentStatus && !!allStatuses,
    staleTime: 1000 * 60 * 5,
  });
};

export const useUpdateBookingStatus = () => {
  const updateStatus = async (
    bookingId: string,
    newStatus: string,
    updatedBy?: string,
    notes?: string
  ) => {
    console.log('[UPDATE-BOOKING-STATUS] Updating booking status:', {
      bookingId,
      newStatus,
      updatedBy,
      notes
    });

    const { data, error } = await supabase.rpc('update_booking_status', {
      p_booking_id: bookingId,
      p_new_status: newStatus,
      p_updated_by: updatedBy || null,
      p_notes: notes || null
    });

    if (error) {
      console.error('[UPDATE-BOOKING-STATUS] Error updating booking status:', error);
      console.error('[UPDATE-BOOKING-STATUS] Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    console.log('[UPDATE-BOOKING-STATUS] Status update result:', data);
    return data;
  };

  return { updateStatus };
};
